import { useEffect, useRef, useState } from 'react';
import { parseAttribution } from '../../shared/tracking';

export function VisitTracking({ slug, stage }: { slug: string; stage: 'locked' | 'opened' }) {
  const visitId = useRef(crypto.randomUUID());
  const [state, setState] = useState<'pending' | 'recorded' | 'off' | 'failed'>('pending');
  const attribution = parseAttribution(window.location.search);
  const privacyPreference = navigator.doNotTrack === '1' || (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
  const disabled = attribution.disabled || privacyPreference;
  useEffect(() => {
    if (disabled) { setState('off'); return; }
    let cancelled = false;
    let running = false;
    let attempts = 0;
    let timer: number | undefined;
    const report = async () => {
      if (document.visibilityState !== 'visible' || cancelled || running) return;
      running = true;
      attempts++;
      try {
        const response = await fetch(`/api/public/${slug}/visit`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: visitId.current, stage, search: window.location.search.slice(0, 2048) }) });
        if (!response.ok) throw new Error('Visit not saved');
        const result = await response.json() as { recorded: boolean };
        if (!cancelled) setState(result.recorded ? 'recorded' : 'off');
      } catch {
        if (!cancelled) {
          running = false;
          setState('failed');
          if (attempts < 3) timer = window.setTimeout(() => void report(), 2000 * attempts);
        }
      }
    };
    void report();
    document.addEventListener('visibilitychange', report);
    return () => { cancelled = true; window.clearTimeout(timer); document.removeEventListener('visibilitychange', report); };
  }, [slug, stage, disabled]);
  return <aside className="visit-notice"><details><summary>About visit tracking</summary><p>{disabled || state === 'off' ? 'Visit tracking is off for this page.' : state === 'failed' ? 'Visit activity could not be saved. You can still read and respond to this proposal.' : 'This proposal records when its access screen or content is opened, with any label or campaign details supplied in the link.'} No tracking cookies, IP addresses or device fingerprints are stored in the visit log. A link label does not verify your identity. Reloads count as new visits; reading time and individual sections are not tracked.</p><p>The access-code cookie is used only to remember that you unlocked the proposal. Browser privacy signals (Do Not Track / Global Privacy Control) turn off visit reporting.</p></details></aside>;
}
